import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { leaveGuarded } from './unsavedCore';

/**
 * Every popup closes with Escape (owner 2026-10-22 14:31: „… i klawisz ESC też niech je wyłącza”); the "×" he asked
 * for first was dropped at 14:47 in favour of the dialog's own "Anuluj" / "Zamknij", always in sight (styles.css).
 *
 * Escape: one listener on the document keeps a stack of open layers (dialogs, sheets, "…" menus, popovers) and closes
 * only the topmost. It runs after the fields' own handlers, so an Escape a field already used (a picker closing its
 * list, a row dropping its edit — they call `preventDefault`) is left alone; the Escape it takes is marked
 * (`preventDefault`), so the event card around a dialog (`SidePanel`) and the calendar's shortcuts do not act on it too.
 */

/** `onEscape` returns `false` when it leaves this Escape alone (e.g. the card, from a field): then it is not marked. */
type Layer = { onEscape: React.MutableRefObject<(e: KeyboardEvent) => boolean | void>; base: boolean };
const stack: Layer[] = [];

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  if (top.onEscape.current(e) !== false) e.preventDefault();
}

/**
 * While `active`, Escape calls `onEscape` when this is the topmost open layer. Layers stack in the order they open
 * (a layout effect: in the stack before the next key press; in one commit, in tree order — so a dialog's `DialogLayer`
 * goes first in it, before any popup of its own). `base`: a layer under all the others whenever it opened (the event
 * card, review I1): a bell or "Pomoc" opened before the card still closes first.
 */
export function useEscapeLayer(onEscape: (e: KeyboardEvent) => boolean | void, active = true, { base = false }: { base?: boolean } = {}): void {
  const ref = useRef(onEscape);
  ref.current = onEscape;
  useLayoutEffect(() => {
    if (!active) return;
    const layer: Layer = { onEscape: ref, base };
    if (stack.length === 0) document.addEventListener('keydown', onKeyDown);
    if (base) {
      const firstOther = stack.findIndex((l) => !l.base);
      stack.splice(firstOther < 0 ? stack.length : firstOther, 0, layer);
    } else stack.push(layer);
    return () => {
      const at = stack.indexOf(layer);
      if (at >= 0) stack.splice(at, 1);
      if (stack.length === 0) document.removeEventListener('keydown', onKeyDown);
    };
  }, [active, base]);
}

/** Where the focus goes when the opener has left the page (review I2): the heading of the card or dialog it was in, or the page's main area. */
function fallbackFocus(scope: HTMLElement | null) {
  const area = scope?.isConnected ? scope : document.querySelector<HTMLElement>('main');
  if (!area) return;
  const heading = area.tagName === 'MAIN' ? null : area.querySelector<HTMLElement>('h2, h1');
  const target = heading ?? area;
  // Only while it holds the focus (owner 2026-10-23 16:14): a `tabindex` left on the page's main area makes Safari focus
  // it on every click of a button inside (Safari does not focus clicked buttons) — and a "…" menu, seeing its focus go
  // outside, closed before its item got the click: the second "Usuń wydarzenie" did nothing.
  if (target.tabIndex < 0 && !target.hasAttribute('tabindex')) {
    target.tabIndex = -1;
    target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
  }
  target.focus({ preventScroll: true });
}

/**
 * Remembers what had focus when the popup opened and gives the focus back when it closes — unless the focus has
 * meanwhile been put somewhere on purpose (only from nowhere: the body, after the popup left the page). When the
 * opener itself is gone (a deleted row), the focus goes to the card around it, or to the main area — never the body.
 */
export function useReturnFocus(active = true): void {
  // read while rendering, before the popup's own field takes the focus (`autoFocus`)
  const [from] = useState(() => {
    const opener = typeof document !== 'undefined' && document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    return { opener, scope: opener?.closest<HTMLElement>('[role="dialog"], [role="alertdialog"], aside, main') ?? null };
  });
  useEffect(
    () => () => {
      if (!active) return;
      const now = document.activeElement;
      if (now && now !== document.body) return;
      if (from.opener?.isConnected) from.opener.focus({ preventScroll: true });
      else if (from.opener) fallbackFocus(from.scope);
    },
    [from, active],
  );
}

/**
 * A dialog's Escape and focus return (owner 2026-10-22 14:31, and 14:47: no "×" — the dialog's own "Anuluj" / "Zamknij"
 * in its footer, always in sight). Put it first inside the dialog. `onClose` does what "Anuluj" does; `busy` (a save
 * under way) makes Escape wait instead of dropping the save.
 *
 * Escape with unsaved changes in the dialog asks first (owner 2026-10-23 21:14, every dialog alike): it goes through
 * `leaveGuarded`, scoped to the dialog it is in — found from a hidden mark it leaves (`data-dialog-layer`), so a clean
 * dialog over a changed form outside it still closes at once.
 *
 * `returnFocus={false}`: a dialog that shows a layer while loading and another with its form (review M5) records the
 * opener once, in its shell (`useReturnFocus()` before it takes the focus) — the form's layer, mounted after the
 * focus moved into the dialog, would otherwise take the dialog itself for the opener.
 */
export function DialogLayer({ onClose, busy = false, returnFocus = true }: { onClose: () => void; busy?: boolean | undefined; returnFocus?: boolean }) {
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const mark = useRef<HTMLSpanElement>(null);
  useEscapeLayer(() => {
    if (!busyRef.current) leaveGuarded(onClose, dialogOf(mark.current));
  });
  useReturnFocus(returnFocus);
  return <span ref={mark} hidden data-dialog-layer="" />;
}

/** What a dialog's Escape closes: the dialog box around the mark (or, outside one, the element it is in). */
export function dialogOf(el: Element | null): Element | null {
  if (!el) return null;
  return el.closest('[role="dialog"], [role="alertdialog"], .dialog') ?? el.parentElement;
}
